import { usePathname, useRouter } from "expo-router";
import React, { useEffect, useMemo } from "react";

import { useAuth } from "./Auth";
import { useCurrentUser } from "./CurrentUser";

import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";

/**
 * Props for GateProvider component
 * @property root0.children - Child components rendered after user validation gate passes
 */
interface Props {
  children: React.ReactNode;
}

/**
 * Result of sign-in confirmation flow (unused in Gate, kept for compatibility)
 * @property success - Whether sign-in confirmation was successful
 * @property session - Session token if confirmation successful
 */
export interface ConfirmSignInResult {
  success: boolean;
  session: string | null | undefined;
}

const GateContext = React.createContext<boolean>(false);

/**
 * Gate provider component validating user account completeness
 *
 * Validates current user using validationCodes from AuthProvider and redirects
 * to registration if validation codes exist. Calls validateGoal for validated users.
 * @param root0 - Component props
 * @param root0.children - Child components rendered after user validation gate passes
 */
export /**
 *
 */
const GateProvider = ({ children }: Props) => {
  const currentUser = useCurrentUser();
  const auth = useAuth();
  const validationCodes = useMemo(
    () => auth?.validationCodes ?? [],
    [auth?.validationCodes]
  );
  const validateGoal = useRecordUserGoal({
    goalType: GoalType.ValidateCurrentUser,
    value: currentUser?.id ?? "",
  });
  const router = useRouter();
  const pathName = usePathname();
  const isOnRegisterScreen = pathName.includes("register");

  useEffect(() => {
    if (currentUser) {
      validateGoal();

      if (validationCodes.length > 0) {
        if (!isOnRegisterScreen) {
          router.replace(`/register?codes=${validationCodes.join(",")}`);
        }
      } else {
        if (isOnRegisterScreen) {
          setTimeout(() => router.replace("/"), 100);
        }
      }
    }
  }, [currentUser, isOnRegisterScreen, router, validateGoal, validationCodes]);

  return (
    <GateContext.Provider value>
      {/* Prefetch custom metrics for all authenticated users */}
      {/* {isValidated && <CustomMetricsPrefetch />} */}
      {children}
    </GateContext.Provider>
  );
};
